/**
 * Iter 313 VA-1b — Owner View Through frontend context.
 *
 * Product model (from the accepted design):
 *   • Owner/partner selects a viewed_context (role preset OR specific user).
 *   • The selected context controls what UI renders and what actions gate.
 *   • The REAL owner remains the actor for every write and every audit.
 *   • This is NOT impersonation. This is NOT a read-only preview. Actions
 *     execute if the selected context is permitted — but audit records the
 *     real owner as actor and the selected context as viewed_through.
 *
 * Wiring:
 *   • Provider mounts at app root, syncs to localStorage.
 *   • Axios request interceptor injects `X-View-Through` header when active.
 *   • Axios response interceptor surfaces `viewed_context_denied` 403 as a
 *     neutral toast; passes other errors through unchanged.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const STORAGE_KEY = "view_through_context_v1";
const HEADER_NAME = "X-View-Through";

const ViewThroughContext = createContext({
  viewThrough: null,
  isActive: false,
  enter: () => {},
  exit: () => {},
  refresh: () => {},
});

const encodeHeader = (payload) => {
  try {
    return btoa(JSON.stringify(payload)).replace(/=+$/, "").replace(/\+/g, "-").replace(/\//g, "_");
  } catch (_e) {
    return null;
  }
};

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.target_type || !parsed.target_key) return null;
    return parsed;
  } catch (_e) {
    return null;
  }
};

let _interceptorsInstalled = false;
let _currentContext = null; // module-scoped mirror so interceptors see the latest without closure staleness

const installInterceptors = () => {
  if (_interceptorsInstalled) return;
  _interceptorsInstalled = true;
  axios.interceptors.request.use((config) => {
    if (_currentContext) {
      const encoded = encodeHeader({
        target_type: _currentContext.target_type,
        target_key: _currentContext.target_key,
      });
      if (encoded) {
        config.headers = config.headers || {};
        config.headers[HEADER_NAME] = encoded;
      }
    } else if (config?.headers && config.headers[HEADER_NAME]) {
      delete config.headers[HEADER_NAME];
    }
    return config;
  });
  axios.interceptors.response.use(
    (r) => r,
    (error) => {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      // Backend signals a context-scoped denial when the viewed context
      // lacks permission. Prefer a structured `detail.friendly` sentence
      // (Perm-Drift-Fix 2026-07-09) so the operator sees the exact missing
      // permission ("...because On-Site Staff Calendar read is off") instead
      // of the historical generic phrasing.
      if (status === 403 && _currentContext) {
        const label = _currentContext.target_label || "This context";
        // Structured detail from backend: { code, missing_permission, friendly }
        if (detail && typeof detail === "object" && detail.friendly) {
          toast.error(`${label}: ${detail.friendly}`);
        } else if (typeof detail === "string" && detail.trim()) {
          toast.error(`${label}: ${detail}`);
        } else {
          toast.error(`${label} isn't permitted to do that.`);
        }
      }
      return Promise.reject(error);
    },
  );
};

export const ViewThroughProvider = ({ children }) => {
  const [viewThrough, setViewThrough] = useState(readStored);

  useEffect(() => {
    _currentContext = viewThrough;
    if (viewThrough) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(viewThrough));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [viewThrough]);

  useEffect(() => {
    installInterceptors();
  }, []);

  const enter = useCallback((target) => {
    // target: { target_type: "role"|"user", target_key, target_label }
    if (!target?.target_type || !target?.target_key) return;
    setViewThrough({
      target_type: target.target_type,
      target_key: target.target_key,
      target_label: target.target_label || `${target.target_type}: ${target.target_key}`,
      activated_at: new Date().toISOString(),
    });
  }, []);

  const exit = useCallback(() => {
    setViewThrough(null);
  }, []);

  const value = useMemo(() => ({
    viewThrough,
    isActive: !!viewThrough,
    enter,
    exit,
    refresh: () => setViewThrough(readStored()),
  }), [viewThrough, enter, exit]);

  return (
    <ViewThroughContext.Provider value={value}>{children}</ViewThroughContext.Provider>
  );
};

export const useViewThrough = () => useContext(ViewThroughContext);

/**
 * View Through — Supported Route Contract.
 *
 * The canonical list of route paths where View Through is genuinely wired
 * end-to-end. A route qualifies iff BOTH:
 *   (a) the backend handler(s) it hits resolve the viewed context via
 *       the centralised `get_effective_actor` primitive (VA-1c/VA-1d), and
 *   (b) the frontend component reads any permission-gated visibility from
 *       the canonical effective-permissions grid
 *       (`GET /api/portal/effective-permissions`) or from a config
 *       endpoint whose `can_*` fields are computed against the effective
 *       user.
 *
 * Anti-drift rule (VA-1d, 2026-07-13):
 * Only EXACT route paths are supported. Wholesale portal roots such as
 * `/portal`, `/portal/home`, or role landing routes are NEVER added,
 * because they may conditionally render un-migrated surfaces
 * (Receptionist Mode, Owner dashboards, etc.). Each new entry must land
 * with a paired workflow-parity CI class in
 * `backend/tests/test_iter31X_*_workflow_parity.py`.
 *
 * Everything else is UNSUPPORTED under View Through and the fail-closed
 * shield takes over — no silent execution against owner permissions.
 */
export const VIEW_THROUGH_SUPPORTED_PATHS = Object.freeze([
  // Staff Preview / On-Site Staff Calendar (Iter 313 VA-1c, 2026-07-13).
  "/portal/receptionist/redundant",
  "/portal/floor",
  // Staff Calendar (Iter 314 VA-1d, 2026-07-13).
  // Backend surface: routes.calendar, routes.calendar_cleanup,
  // routes.appointment_outcomes, routes.waitlist queue-write endpoints,
  // routes.staff_profiles::/technicians/mapping, routes.customers
  // by-phone GET + DELETE + autocomplete.
  // Frontend surface: StaffCalendarsView.jsx + EventDetailModal.jsx +
  // AppointmentOutcomeSection.jsx + AddEventModal / EditEventModal /
  // WaitlistPanel / WaitingCommitmentsStrip / PendingReviewSheet.
  // Parity CI: test_iter314_staff_calendar_workflow_parity.py.
  "/portal/calendars",
  // Receptionist Mode (Iter 315 VA-1e, 2026-07-13).
  // Backend surface: routes.receptionist_callbacks, routes.receptionist_inbound,
  // routes.receptionist_alerts — all authorize on ctx.effective_user;
  // audit stays with ctx.real_actor (owner under VT).
  // Frontend surface: ReceptionistOperatingView.jsx reads isOwner /
  // isTrusted from useEffectivePermissions().
  // Parity CI: test_iter315_receptionist_mode_workflow_parity.py.
  "/portal/receptionist",
]);

/**
 * Returns { isActive, isSupported } for the current pathname when a View
 * Through context is set. `isActive=false` short-circuits everything; the
 * shield only ever renders when `isActive && !isSupported`.
 */
export const isPathSupportedByViewThrough = (pathname) => {
  if (!pathname) return false;
  return VIEW_THROUGH_SUPPORTED_PATHS.some((p) =>
    pathname === p || pathname.startsWith(p + "/")
  );
};



// Test-only utility to reset module state between suites.
export const _resetForTests = () => {
  _interceptorsInstalled = false;
  _currentContext = null;
};
